import { forwardRef, memo } from 'react';
import map from 'lodash/map';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import Typography from '@mui/material/Typography';
// Skillmore UI Components
import GridBox from '@empathco/ui-components/src/mixins/GridBox';
import YesNoButtonGroup from '@empathco/ui-components/src/elements/YesNoButtonGroup';
// local imports
import { EmailNotificationType, EmailNotificationReducedType } from '../constants/emailNotifications';
import PreferencesCheckbox from '../v3/PreferencesCheckbox';

export interface PreferencesRowChoice {
  id: EmailNotificationType | EmailNotificationReducedType;
  text: string;
}

// TODO: convert to generic (value & onChange; values & onValuesChange)
type PreferencesRowProps = {
  readonly prompt: string;
  readonly value?: boolean;
  readonly values?: Partial<Record<EmailNotificationType | EmailNotificationReducedType, boolean>> | null;
  readonly choices?: readonly PreferencesRowChoice[];
  readonly onChange?: (id: boolean) => void;
  readonly onValuesChange?: (id: string) => void;
  readonly disabled?: boolean | null;
}

const PreferencesRow = forwardRef<HTMLDivElement, PreferencesRowProps>(({
  prompt,
  value = false,
  values,
  choices,
  onChange,
  onValuesChange,
  disabled = false
}, ref) => {
  const hasChoices = Boolean(choices);
  return (
    <GridBox
        ref={ref}
        container
        pt={hasChoices ? 1 : 2}
        pb={hasChoices ? 3 : 2}
    >
      <GridBox
          container
          size={{
            xs: 12,
            sm: hasChoices ? undefined : 6,
            md: hasChoices ? undefined : 7
          }}
          alignItems="center"
          py={hasChoices ? 2 : 1}
          pr={hasChoices ? undefined : 2}
      >
        <Typography variant="subtitle1">
          <FormattedMessage id={prompt}/>
        </Typography>
      </GridBox>
      {map(choices || [{} as PreferencesRowChoice], ({ id, text }, index) => (
        <GridBox
            key={index}
            container
            size={{
              xs: 12,
              sm: hasChoices ? undefined : 6,
              md: hasChoices ? 6 : 5,
              lg: hasChoices ? undefined : 4,
              xl: hasChoices ? 4 : 3
            }}
            alignItems="center"
            py={hasChoices ? undefined : 1}
        >
          {id ? onValuesChange && (
            <PreferencesCheckbox
                id={id}
                title={text}
                value={Boolean(values && values[id])}
                onChange={onValuesChange}
                disabled={disabled}
            />
          ) : onChange && (
            <YesNoButtonGroup ariaLabel={prompt} value={value} onChange={onChange} disabled={disabled}/>
          )}
        </GridBox>
      ))}
    </GridBox>
  );
});

PreferencesRow.displayName = 'PreferencesRow';

export default memo(PreferencesRow);
